/** @jsxImportSource @emotion/react */
import { Delete, Edit, ExpandLess, ExpandMore } from '@mui/icons-material'
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import { riskDeleteWorkspaceAtom } from '@app/state/riskAtoms'
import { useAtom } from 'jotai'
import { useMemo, useState } from 'react'

import { toast } from '@app/components/ui/Toast/toast'
import { appLink } from '@app/config/appLink'
import type { RiskWorkspace } from '@app/types'
import { useNavigate } from 'react-router-dom'
import { workspaceDisplayAuthor, workspaceHasNoValidLayers } from '../utils/Workspaces.utils'
import {
  tableCellStyle,
  tableContainerStyle,
  tableHeaderCellStyle,
  tableHeadStyle,
} from './WorkspaceTable.styles'
import { navButtonStyle } from '../WorkspaceNavbar/WorkspaceNavbar.styles'
import { Dialog, DialogContent } from '@app/components/ui/Dialog/Dialog'
import { AxiosError } from 'axios'
import { useSelector } from 'react-redux'
import { RootState } from '@app/redux/store'

type WorkspaceVisibility = 'private' | 'organisation'

interface WorkspaceTableProps {
  workspaces: RiskWorkspace[]
  selectedWorkspace: RiskWorkspace | null
  onSelected: (selectedWorkspace: RiskWorkspace | null) => void
  onEditWorkspace?: (workspace: RiskWorkspace) => void
  onDeleteWorkspace?: (workspace: RiskWorkspace) => void
}

type WorkspacesByVisibility = Record<WorkspaceVisibility, RiskWorkspace[]>

const groupWorkspacesByVisibility = (workspaces: RiskWorkspace[]): WorkspacesByVisibility => {
  return workspaces.reduce<WorkspacesByVisibility>(
    (acc, state) => {
      const visibility = (state.visibility as WorkspaceVisibility) || 'private'
      acc[visibility].push(state)
      return acc
    },
    { private: [], organisation: [] },
  )
}

export const WorkspacesSavedTable: React.FC<WorkspaceTableProps> = ({
  workspaces,
  selectedWorkspace,
  onSelected,
}) => {
  const theme = useTheme()
  const { layerData } = useSelector((state: RootState) => state.riskMap)
  const navigate = useNavigate()
  const [{ mutate: deleteWorkspace }] = useAtom(riskDeleteWorkspaceAtom)
  const [visibilityFilter, setVisibilityFilter] = useState<WorkspaceVisibility>('private')

  const [sortBy, setSortBy] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [workspaceToDelete, setWorkspaceToDelete] = useState<RiskWorkspace | null>(null)

  const workspacesByVisibility = useMemo(
    () => groupWorkspacesByVisibility(workspaces),
    [workspaces],
  )

  const onSort = (column: string) => {
    if (sortBy === column) {
      // toggle sort direction
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else {
        setSortBy(null)
      }
    } else {
      // set new sort column
      setSortBy(column)
      setSortDirection('asc')
    }
  }

  const filteredWorkspaces = useMemo(() => {
    const rows = workspacesByVisibility[visibilityFilter] ?? []
    return rows.toSorted((a, b) => {
      let compare = 0
      if (sortBy === 'name') {
        compare = a.name.localeCompare(b.name)
      } else if (sortBy === 'created_at') {
        compare = new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      } else if (sortBy === 'author') {
        compare = workspaceDisplayAuthor(a).localeCompare(workspaceDisplayAuthor(b))
      }
      return sortDirection === 'asc' ? compare : -compare
    })
  }, [workspacesByVisibility, visibilityFilter, sortBy, sortDirection])

  const handleVisibilityChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: WorkspaceVisibility | null,
  ) => {
    if (!newValue) return
    setVisibilityFilter(newValue)
    onSelected(null)
  }

  const handleRowClick = (workspace: RiskWorkspace) => {
    onSelected(workspace)
  }

  const handleDeleteClick = (event: React.MouseEvent, workspace: RiskWorkspace) => {
    event.stopPropagation()
    setWorkspaceToDelete(workspace)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (!workspaceToDelete) return
    deleteWorkspace(workspaceToDelete.id, {
      onSuccess: () => {
        if (selectedWorkspace?.id === workspaceToDelete.id) {
          onSelected(null)
        }
        toast({
          title: (
            <span>
              <b>{workspaceToDelete.name}</b> has been deleted
            </span>
          ),
          duration: 3000,
          type: 'success',
          position: 'top-right',
        })
        navigate(appLink.riskWorkspaces.path)
      },
      onError: (error: unknown) => {
        const errorMessage = error instanceof AxiosError ? error.response?.data?.error : undefined
        toast({
          title: (
            <span>
              Failed to delete Workspace: <b>{workspaceToDelete.name}</b>
            </span>
          ),
          description: errorMessage,
          duration: 4000,
          type: 'error',
          position: 'top-right',
        })
      },
    })
    setDeleteDialogOpen(false)
    setWorkspaceToDelete(null)
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
    setWorkspaceToDelete(null)
  }
  const privateButtonText = `Personal`
  const organisationButtonText = `Organisation`

  const showActionsColumn = filteredWorkspaces.some((ws) => ws.write_permissions)

  return (
    <Box display="flex" flexDirection="column" flexGrow={1} padding="8px 0 8px 8px" height="100%">
      <Box mb={1} display="flex" gap="8px">
        <Button
          onClick={(e) => handleVisibilityChange(e, 'private')}
          variant="contained"
          css={[
            navButtonStyle(theme, visibilityFilter === 'private', 'dark', privateButtonText.length),
            { fontSize: '14px' },
          ]}
        >
          {privateButtonText}
        </Button>
        <Button
          onClick={(e) => handleVisibilityChange(e, 'organisation')}
          variant="contained"
          css={[
            navButtonStyle(
              theme,
              visibilityFilter === 'organisation',
              'dark',
              organisationButtonText.length,
            ),
            { fontSize: '14px' },
          ]}
        >
          {organisationButtonText}
        </Button>
      </Box>
      <Box flexGrow={1} position="relative" minHeight={0}>
        <TableContainer css={tableContainerStyle(theme)}>
          <Table>
            <TableHead css={tableHeadStyle()}>
              <TableRow>
                <TableCell
                  css={tableHeaderCellStyle(theme)}
                  onClick={() => onSort('name')}
                  sx={{ cursor: 'pointer' }}
                >
                  <Box>
                    Name
                    {sortBy === 'name' &&
                      (sortDirection === 'asc' ? <ExpandMore /> : <ExpandLess />)}
                  </Box>
                </TableCell>
                <TableCell onClick={() => onSort('created_at')} css={tableHeaderCellStyle(theme)}>
                  <Box>
                    Created
                    {sortBy === 'created_at' &&
                      (sortDirection === 'asc' ? <ExpandMore /> : <ExpandLess />)}
                  </Box>
                </TableCell>
                <TableCell onClick={() => onSort('author')} css={tableHeaderCellStyle(theme)}>
                  <Box>
                    Author
                    {sortBy === 'author' &&
                      (sortDirection === 'asc' ? <ExpandMore /> : <ExpandLess />)}
                  </Box>
                </TableCell>
                {showActionsColumn && (
                  <TableCell css={tableHeaderCellStyle(theme)}>Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredWorkspaces.length === 0 ? (
                <TableRow>
                  <TableCell css={tableCellStyle(theme)} colSpan={4}>
                    No saved states available for this visibility.
                  </TableCell>
                </TableRow>
              ) : (
                filteredWorkspaces.map((workspace) => {
                  const isWorkspaceInvalid = Boolean(
                    layerData && workspaceHasNoValidLayers(workspace, layerData),
                  )

                  return (
                    <TableRow
                      key={workspace.id}
                      hover
                      selected={selectedWorkspace?.id === workspace.id}
                      onClick={() => handleRowClick(workspace)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell css={tableCellStyle(theme, isWorkspaceInvalid)}>
                        {workspace.name}
                      </TableCell>
                      <TableCell css={tableCellStyle(theme, isWorkspaceInvalid)}>
                        {new Date(workspace.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell css={tableCellStyle(theme, isWorkspaceInvalid)}>
                        {workspaceDisplayAuthor(workspace)}
                      </TableCell>
                      {showActionsColumn && (
                        <TableCell
                          css={{ ...tableCellStyle(theme), padding: '0 !important' }}
                          align="right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {workspace.write_permissions && (
                            <Box display="flex" justifyContent="flex-end" gap="8px">
                              <IconButton
                                aria-label="Edit saved state"
                                size="small"
                                disabled={isWorkspaceInvalid}
                                title="Edit saved state"
                                css={{
                                  opacity: isWorkspaceInvalid ? 0.3 : 1,
                                }}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  navigate(`/app/risk/explorer/workspaces/${workspace.id}/edit`)
                                }}
                              >
                                <Edit fontSize="small" color="info" />
                              </IconButton>
                              <IconButton
                                aria-label="Delete saved state"
                                size="small"
                                title="Delete saved state"
                                onClick={(e) => handleDeleteClick(e, workspace)}
                              >
                                <Delete fontSize="small" color="info" />
                              </IconButton>
                            </Box>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {deleteDialogOpen && (
        <Dialog open>
          <DialogContent showCloseButton={false}>
            <Box
              display="flex"
              flexDirection="column"
              padding="24px"
              gap="8px"
              borderBottom={`1px solid ${theme.palette.grey[100]}80`}
            >
              <Typography
                variant="h6"
                css={{ fontWeight: 700, fontSize: '16px', color: theme.palette.primary.main }}
              >
                Are you sure you want to delete {workspaceToDelete?.name}?
              </Typography>
              <span>
                This action cannot be undone. The Workspace will be
                <br />
                permanently deleted.
              </span>
            </Box>
            <Box display="flex" justifyContent="flex-end" gap="16px" padding="16px">
              <Button onClick={handleDeleteCancel} color="blue" variant="outlined">
                Cancel
              </Button>
              <Button onClick={handleDeleteConfirm} color="blue" variant="contained">
                Delete
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  )
}
